/**
 * Background Jobs — WHAT should run.
 *
 * Two rules from the integration guide drive this form:
 *  · `executionConfig` is polymorphic AND strict (`extra="forbid"`), so the config
 *    is rebuilt from scratch whenever executionType changes. Merging guarantees 422.
 *  · `jobKey` is immutable after creation — it is the handle schedules and run
 *    history refer to — so the edit form renders it disabled.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppNavigate } from '../../../hooks/useAppNavigate';
// InputNumber and ConfirmDialog come straight from PrimeReact — sharedUi has no
// wrapper for either. Everything else here goes through the shared wrappers.
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'sharedUi/Dialog';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import { Message } from 'sharedUi/Message';
import { formatDate } from 'sharedUi/dateUtils';
import SaasDataTable, { type SaasColumn } from '../../../components/common/saas-data-table';
import { ProjectCell } from '../../../components/common/project-cell';
import { ListCount } from '../../../components/common/list-count';
import { backgroundJobService } from '../../../services/background-job.service';
import { projectsService, type AccessibleProject } from '../../../services/projects.service';
import type { BackgroundJob } from '../../../types/background-job.types';
import { describeConfig } from './shared';
import { JsonField } from '../../../components/common/json-field';
import { useToastApi } from '../../../hooks/use-toast-api';
import { useExecutionTypes } from './use-job-value-sets';
import { useJobPermissions } from './use-job-permissions';

const JobsTab = () => {
  const { canCreate, canUpdate, canDelete, canExecute } = useJobPermissions();
  const navigate = useAppNavigate();
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  const { options: executionTypeOptions } = useExecutionTypes();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  // Which project's jobs to show. `appId` on a job is a real, client-settable
  // field (the API validates it against project_master), so this filters rather
  // than merely decorating. Empty = every project the caller can reach.
  const [appFilter, setAppFilter] = useState('');
  const [projects, setProjects] = useState<AccessibleProject[]>([]);
  // Gates the FILTER only — with one project it could never be anything but a
  // no-op. The Project column itself is unconditional.
  const canFilterByProject = projects.length > 1;

  useEffect(() => {
    projectsService.getAccessible().then(setProjects).catch(() => setProjects([]));
  }, []);
  // without this the API never returns soft-deleted jobs, so Restore is unreachable
  const showDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  // Newest first until the viewer picks a column.
  const [sort, setSort] = useState<{ field: string | null; order: 1 | -1 | null }>({ field: 'createdAt', order: -1 });
  /* CORE = the shared jobs that belong to no project (appId: null). Off, the list
     is the project's own rows; on, the core rows come too — the same meaning the
     Log Master screen's "Show core" has. Server-side, because a client-side filter
     would only sift the page in hand and leave the count and pager describing a
     different list. */
  const [showCore, setShowCore] = useState(false);

  // Run-now needs a target plus a small form, so it does not fit in a
  // confirmDialog the way delete does.
  const [runTarget, setRunTarget] = useState<BackgroundJob | null>(null);
  // Which row opened the kebab — the menu is a single popup, so it needs telling.
  const [menuJob, setMenuJob] = useState<BackgroundJob | null>(null);
  const menuRef = useRef<Menu>(null);
  const [runParams, setRunParams] = useState<Record<string, unknown> | undefined>({});

  // The function list lives on the form PAGE that uses it (job-form.tsx), which
  // reads it from /background-jobs/function-keys — the same rows the executor
  // resolves against. It was fetched here too, for a dropdown this tab no longer
  // renders.

  const listQ = useQuery({
    queryKey: ['bg-jobs', { page, pageSize, search, typeFilter, appFilter, showCore, showDeleted, sort }],
    queryFn: () => backgroundJobService.listJobs({
      page,
      page_size: pageSize,
      search: search || undefined,
      executionType: typeFilter || undefined,
      appId: appFilter || undefined,
      // Sent either way: explicitly false is what asks the server to leave the
      // core rows OUT, which is the toggle's whole point in its off position.
      include_global: showCore,
      include_deleted: showDeleted || undefined,
      sort_by: sort.field || undefined,
      sort_order: sort.order === 1 ? 'asc' : sort.order === -1 ? 'desc' : undefined,
    }),
    staleTime: 30_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['bg-jobs'] });
    qc.invalidateQueries({ queryKey: ['bg-schedules'] });
  };

  const deleteM = useMutation({
    mutationFn: (id: string) => backgroundJobService.deleteJob(id),
    onSuccess: () => { ok('Job deleted'); invalidate(); },
    onError: (e) => fail(e),
  });

  const restoreM = useMutation({
    mutationFn: (id: string) => backgroundJobService.restoreJob(id),
    onSuccess: () => { ok('Job restored'); invalidate(); },
    onError: (e) => fail(e),
  });

  const runM = useMutation({
    mutationFn: ({ id, parameters }: { id: string; parameters?: Record<string, unknown> }) =>
      backgroundJobService.runNow(id, parameters),
    onSuccess: () => {
      ok('Run started — this does not shift the schedule');
      setRunTarget(null);
      qc.invalidateQueries({ queryKey: ['bg-runs'] });
      qc.invalidateQueries({ queryKey: ['bg-activity'] });
      qc.invalidateQueries({ queryKey: ['bg-dashboard'] });
    },
    onError: (e) => fail(e),
  });

  // Create and edit are PAGES now (see job-form.tsx), not a modal on this tab —
  // the shape every other master uses, so leaving with unsaved edits is guarded by
  // useNavigationBlocker instead of being lost to a backdrop click.
  const openCreate = () => navigate('/background-jobs/jobs/create');
  const openEdit = (j: BackgroundJob) => navigate(`/background-jobs/jobs/${j._id}/edit`);

  const columns: SaasColumn<BackgroundJob>[] = useMemo(() => [
    {
      field: 'jobKey', header: 'Job Key', sortable: true, hideable: false, style: { minWidth: 190 },
      body: (j) => (
        <span className="font-mono text-xs font-semibold text-ink">
          {j.jobKey}{j.isDelete && <Tag value="DELETED" severity="danger" className="ml-2" />}
        </span>
      ),
    },
    { field: 'jobName', header: 'Name', sortable: true, filter: true, style: { minWidth: 200 } },
    {
      field: 'executionType', header: 'Type', sortable: true, style: { width: 130 },
      body: (j) => <Tag value={j.executionType} severity={j.executionType === 'FUNCTION' ? 'info' : j.executionType === 'API_CALL' ? 'warning' : 'success'} />,
    },
    {
      field: 'executionConfig', header: 'Runs', style: { minWidth: 240 },
      body: (j) => <span className="text-xs text-ink-muted">{describeConfig(j.executionType, j.executionConfig)}</span>,
    },
    {
      field: 'maxRetries', header: 'Retries', align: 'center', style: { width: 110 },
      body: (j) => <span className="text-xs">{j.maxRetries}{j.maxRetries > 0 ? ` × ${j.retryIntervalSeconds}s` : ''}</span>,
    },
    {
      field: 'isActive', header: 'Status', align: 'center', style: { width: 100 },
      body: (j) => <Tag value={j.isActive ? 'Active' : 'Inactive'} severity={j.isActive ? 'success' : 'secondary'} />,
    },
    // Always shown: a job with no appId is shared across every project, and
    // telling that apart from one scoped to a project matters even to a caller
    // who can only reach one. The FILTER above is the part that needs a choice.
    {
      field: 'appId', header: 'Project', style: { minWidth: 150 },
      body: (j: BackgroundJob) => <ProjectCell appId={j.appId} projects={projects} />,
    },
    {
      field: 'createdAt', header: 'Created At', sortable: true, style: { minWidth: 130 },
      // Date only: the time of creation has never been what anyone scans this
      // column for, and it cost half the width of the row.
      body: (j) => <span className="text-xs text-gray-500">{j.createdAt ? formatDate(j.createdAt) : '-'}</span>,
    },
    {
      field: 'actions', header: 'Actions', hideable: false, align: 'center', style: { width: 90 },
      /* One kebab instead of four icons: the row carried up to four buttons and
         250px of chrome for commands used one at a time. The menu is built per
         row on open, so it only ever offers what this job and this caller allow. */
      body: (j) => (
        <div className="flex items-center justify-center">
          <Button aria-label="Actions" icon="pi pi-ellipsis-v" text rounded
            data-testid="BackgroundJobs.Action.Menu"
            onClick={(e) => { setMenuJob(j); menuRef.current?.toggle(e); }} />
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canCreate, canUpdate, canDelete, canExecute, runM.isPending, canFilterByProject, projects]);

  /** The kebab's model, rebuilt for whichever row opened it. */
  const menuItems: MenuItem[] = useMemo(() => {
    const j = menuJob;
    if (!j) return [];
    /* action-menu-* are the shared classes that colour a kebab's icons (index.css,
       the same ones the Units list uses): view green, edit indigo, delete red,
       add purple. Reused rather than restyled so every kebab in the app reads the
       same way. */
    const items: MenuItem[] = [
      { label: 'View', icon: 'pi pi-eye', className: 'action-menu-view',
        command: () => navigate(`/background-jobs/jobs/${j._id}`) },
    ];
    if (canExecute && !j.isDelete) {
      items.push({
        label: 'Run now', icon: 'pi pi-play', className: 'action-menu-add', disabled: runM.isPending,
        command: () => { setRunTarget(j); setRunParams(j.defaultParameters || {}); },
      });
    }
    if (canUpdate && !j.isDelete) items.push({ label: 'Edit', icon: 'pi pi-pencil', className: 'action-menu-edit', command: () => openEdit(j) });
    if (canUpdate && j.isDelete) items.push({ label: 'Restore', icon: 'pi pi-replay', className: 'action-menu-add', command: () => restoreM.mutate(j._id) });
    if (canDelete && !j.isDelete) {
      items.push({ separator: true });
      items.push({
        label: 'Delete', icon: 'pi pi-trash', className: 'action-menu-delete',
        command: () => confirmDialog({
          message: `Delete "${j.jobName}"? Its schedules must be removed first.`,
          header: 'Delete job', icon: 'pi pi-exclamation-triangle',
          acceptClassName: 'p-button-danger',
          accept: () => deleteM.mutate(j._id),
        }),
      });
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuJob, canUpdate, canDelete, canExecute, runM.isPending]);


  const rows = listQ.data?.items ?? [];

  return (
    <>
      {toast}
      <ConfirmDialog />
      {/* One popup for the whole table; its model is rebuilt for the row that opened it. */}
      <Menu model={menuItems} popup ref={menuRef} id="job-actions-menu" />

      <SaasDataTable
        tableId="background-jobs"
        columns={columns}
        data={rows}
        loading={listQ.isFetching}
        dataKey="_id"
        emptyMessage="No background jobs yet."
        first={(page - 1) * pageSize}
        rows={pageSize}
        totalRecords={listQ.data?.total ?? 0}
        onPage={(e: { first: number; rows: number }) => { setPageSize(e.rows); setPage(Math.floor(e.first / e.rows) + 1); }}
        rowsPerPageOptions={[10, 25, 50]}
        sortField={sort.field ?? undefined}
        sortOrder={sort.order ?? undefined}
        onSort={(e: { sortField: string; sortOrder: 1 | -1 | null }) => { setSort({ field: e.sortField, order: e.sortOrder }); setPage(1); }}
        filters={{}}
        onFilter={() => { /* server-side search covers this list */ }}
        globalSearch={search}
        onGlobalSearchChange={(v: string) => { setSearch(v); setPage(1); }}
        globalSearchPlaceholder="Search job key or name..."
        testId="BackgroundJobs"
        toolbarStart={
          <div className="flex flex-wrap items-center gap-2">
            <Dropdown
              value={typeFilter}
              options={[{ label: 'All Types', value: '' }, ...executionTypeOptions]}
              optionLabel="label" optionValue="value" placeholder="All Types"
              showClear={!!typeFilter} style={{ minWidth: 160 }}
              onChange={(e: { value: string }) => { setTypeFilter(e.value || ''); setPage(1); }}
            />
            {canFilterByProject && (
              <Dropdown
                value={appFilter}
                options={[
                  { label: 'All Projects', value: '' },
                  ...projects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id })),
                ]}
                optionLabel="label" optionValue="value" placeholder="All Projects"
                showClear={!!appFilter} style={{ minWidth: 170 }}
                onChange={(e: { value: string }) => { setAppFilter(e.value || ''); setPage(1); }}
                data-testid="BackgroundJobs.ProjectFilter"
              />
            )}
            {/* Beside the project filter because it answers the same question —
                whose jobs am I looking at — and it ADDS the shared ones rather
                than replacing the selection. Same control, same wording and same
                meaning as Log Master's. */}
            <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
              <InputSwitch
                checked={showCore}
                onChange={(e) => { setShowCore(!!e.value); setPage(1); }}
                data-testid="BackgroundJobs.Toggle.ShowCore"
              />
              Show core
            </label>
          </div>
        }
        toolbarEnd={
          <>
            <ListCount total={listQ.data?.total} onRefresh={() => listQ.refetch()}
              loading={listQ.isFetching} testId="BackgroundJobs.Refresh" />
            {canCreate && <Button label="New Job" icon="pi pi-plus" onClick={openCreate} />}
          </>
        }
      />

      {/* The create/edit modal that stood here is gone: both are PAGES now
          (job-form.tsx), reached by openCreate/openEdit above. Its state --
          `editing`, `open`, `form`, `submit`, `saveM`, `submitted` -- went with
          it, but the JSX did not, so every render dereferenced names that no
          longer existed and the tab crashed to the error boundary before it
          could paint. */}

      {/* ── Run now ──────────────────────────────────────────────────────────
          The backend takes a per-run parameter override as the TOP layer of the
          merge (job defaults → schedule → this). Firing with a hardcoded `{}` —
          which is what this button used to do — silently discards that layer, so
          a job that needs a date range or a target id could only ever be run with
          its defaults. Pre-filled with the job's defaults so the common case is
          still one click. */}
      <Dialog
        visible={!!runTarget}
        onHide={() => setRunTarget(null)}
        header={runTarget ? `Run ${runTarget.jobKey} now` : ''}
        style={{ width: 560, maxWidth: '95vw' }}
        modal
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" severity="secondary" outlined onClick={() => setRunTarget(null)} />
            <Button label="Run now" icon="pi pi-play" severity="success"
              onClick={() => runM.mutate({ id: runTarget!._id, parameters: runParams })}
              disabled={runM.isPending} />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          {/* NOT "and the schedule's". trigger_background_job calls
              merge_parameters(job, None, overrides) — a manual run has no schedule,
              so scheduleParameters are not applied. Saying otherwise promised that
              Run now reproduces the scheduled fire, when a job whose real behaviour
              is set on its schedule would run with different values. */}
          <JsonField label="Parameters for this run"
            hint="Merged over the job's defaults only — a manual run has no schedule, so schedule parameters do not apply. Leave as-is to use the job's defaults."
            value={runParams}
            onChange={(o) => setRunParams(o || {})} />
          <Message className="w-full justify-start" severity="info"
            text="A manual run takes no schedule claim, so nextRunAt does not move — the scheduled fire still happens as planned." />
          {!runTarget?.isActive && (
            <Message className="w-full justify-start" severity="warn"
              text="This job is inactive. A manual run executes it anyway; only the scheduler skips inactive jobs." />
          )}
        </div>
      </Dialog>

    </>
  );
};

export default JobsTab;
